import React from 'react';
import {
  Card, Col, Row, Spinner,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';

import {
  AgroOverviewLocation,
  AgroOverviewSummary,
  RiskLevel,
} from 'types/Meteorology/Agro/Overview/Dashboard';
import { localidadeComUf } from 'helper/Meteorology/Agro/Overview';
import {
  CloudRainIcon,
  DropIcon,
  ShieldPlusIcon,
} from '../icons';

import './index.scss';

const RISK_LABEL: Record<RiskLevel, string> = {
  baixo: 'Baixo',
  moderado: 'Moderado',
  alto: 'Alto',
  muito_alto: 'Muito alto',
};

const RISK_CLASS: Record<RiskLevel, string> = {
  baixo: 'risk-low',
  moderado: 'risk-mid',
  alto: 'risk-high',
  muito_alto: 'risk-very-high',
};

// Estado vazio (§4 do contrato): o VALOR vira link "Cadastre um local" com a
// mesma fonte/tamanho do valor; a aba CADASTRO acende sozinha pela URL.
// Decisão 10/08/2026: sem sublinhado — a affordance de navegação vem do
// ícone "seta para fora" à DIREITA do texto (só nos cards; a tabela mantém
// o CTA sublinhado sem ícone).
const registerLink = (
  <Link to="/agro/register" className="summary-card-register-link">
    Cadastre um local
    <FontAwesomeIcon
      icon={faArrowUpRightFromSquare}
      className="summary-card-register-icon"
    />
  </Link>
);

const EMPTY_HINT = 'sem local cadastrado';

function SummaryCard({
  label, value, hint, icon, valueClassName,
}: {
  label: string;
  value: React.ReactNode;
  hint: string;
  icon: React.ReactNode;
  valueClassName?: string;
}) {
  return (
    <Card className="agro-overview-summary-card h-100">
      <Card.Body className="d-flex align-items-center gap-3">
        <div className="summary-card-icon">{icon}</div>
        <div className="flex-grow-1">
          <div className="summary-card-label">{label}</div>
          <div className={`summary-card-value ${valueClassName ?? ''}`}>{value}</div>
          <div className="summary-card-hint">{hint}</div>
        </div>
      </Card.Body>
    </Card>
  );
}

export default function SummaryCards({
  summary,
  locations,
  loading,
  isEmptyState,
}: {
  summary: AgroOverviewSummary | undefined;
  locations: AgroOverviewLocation[];
  loading: boolean;
  isEmptyState: boolean;
}) {
  const loadingValue = <Spinner animation="border" size="sm" />;

  const risco = summary?.riscoEstresseHidrico ?? null;
  const localidade = summary?.maiorAcumuladoChuva.localidade ?? null;

  // ADENDO-1 ao §4 do contrato (pós-teste QA de 07/08): os cards 1-2 são da
  // grade nacional (Brasil) e independem de local cadastrado — exibem valor
  // mesmo no estado vazio. O CTA "Cadastre um local" fica nos cards 3-4, que
  // dependem dos locais do usuário.
  const globalCardValue = (value: React.ReactNode) => {
    if (loading) return loadingValue;
    return value;
  };
  const cardValue = (value: React.ReactNode) => {
    if (isEmptyState) return registerLink;
    return globalCardValue(value);
  };
  const cardHint = (hint: string) => (isEmptyState ? EMPTY_HINT : hint);

  // DECISÃO-5: "Últimos 7 dias · Em {localidade}", com sufixo -{UF} quando a
  // UF resolver por match do nome contra a tabela (DECISÃO-2).
  const chuvaHint = localidade
    ? `Últimos 7 dias · Em ${localidadeComUf(localidade, locations)}`
    : 'Últimos 7 dias';

  return (
    <Row className="g-3">
      <Col xs={12} md={6} xl={3}>
        <SummaryCard
          label="ÁREA EM EXCEDENTE HÍDRICO"
          value={globalCardValue(summary ? `${summary.excedenteHidricoPct}%` : '—')}
          hint="Brasil — últimos 7 dias"
          icon={(
            <span className="summary-icon-circle icon-excedente">
              <DropIcon title="Excedente hídrico" />
            </span>
          )}
        />
      </Col>
      <Col xs={12} md={6} xl={3}>
        <SummaryCard
          label="ÁREA EM ESTRESSE HÍDRICO"
          value={globalCardValue(summary ? `${summary.estresseHidricoPct}%` : '—')}
          hint="Brasil — últimos 7 dias"
          icon={(
            <span className="summary-icon-circle icon-estresse">
              <DropIcon title="Estresse hídrico" />
            </span>
          )}
        />
      </Col>
      {/* Ordem decidida em 10/08/2026: MAIOR ACUMULADO (sem "7D" no título)
          vem antes do RISCO. */}
      <Col xs={12} md={6} xl={3}>
        <SummaryCard
          label="MAIOR ACUMULADO DE CHUVA"
          value={cardValue(summary ? `${summary.maiorAcumuladoChuva.mm} mm` : '—')}
          hint={cardHint(chuvaHint)}
          icon={(
            <span className="summary-icon-circle icon-chuva">
              <CloudRainIcon title="Chuva acumulada" />
            </span>
          )}
        />
      </Col>
      <Col xs={12} md={6} xl={3}>
        <SummaryCard
          label="RISCO ESTRESSE HÍDRICO"
          value={cardValue(risco ? RISK_LABEL[risco] : '—')}
          /* Único card de PREVISÃO da linha: o valor vem de
             `risco_estresse_hidrico_7d`. Os outros três são observados. */
          hint={cardHint('Próximos 7 dias')}
          icon={(
            <span className="summary-icon-circle icon-risco">
              <ShieldPlusIcon title="Risco" />
            </span>
          )}
          valueClassName={!isEmptyState && risco ? RISK_CLASS[risco] : undefined}
        />
      </Col>
    </Row>
  );
}
